/**
 * keyFindingsPresentation (KF-REDESIGN-1, William 2026-07-04) — collapse the
 * repeated meta boilerplate that made the Key Findings section "not well
 * structured".
 *
 * Every finding printed three labelled meta rows that were IDENTICAL across the
 * whole section:
 *   INFLUENCES  "Report framing, technology prioritization, and go/no-go recommendation."
 *   WHY KEY     "Ranked #N by materiality to site feasibility and decision impact."
 *               (or "Surfaced as a material constraint or enabler from site evidence analysis.")
 *   SOURCE      GRID
 * Three stacked rows × the same words × N findings = noise that also bloated the
 * block so it could not pack onto the page (the Feasibility-Profile blank tail).
 *
 * This module is a PURE presentation derivation (no I/O, no AI): it classifies a
 * meta value as GENERIC (a known template phrase, or a string ≥2 findings share
 * verbatim) and reduces each finding to ONE compact line — `Source: Grid ·
 * Ranked #1` — keeping only genuinely finding-specific influences/whyKey text.
 * Rendered by ContextualKeyFindingsBlock and mirrored by the DOCX/PPTX emitters
 * so all four surfaces collapse identically.
 */
import type { ContextualKeyFinding } from '../types/siteRag';

/** Known template INFLUENCES phrases (verbatim, whitespace/period-insensitive). */
const GENERIC_INFLUENCES = [
  'report framing, technology prioritization, and go/no-go recommendation',
  'report framing, technology prioritization and go/no-go recommendation',
];

/** Known template WHY-KEY phrases. The "Ranked #N …" family is matched by a
 *  pattern (the N varies) so the rank can be extracted before it is dropped. */
const GENERIC_WHYKEY = [
  'surfaced as a material constraint or enabler from site evidence analysis',
];
const RANKED_WHYKEY_RE = /^ranked\s+#?(\d+)\s+by\s+materiality\s+to\s+site\s+feasibility\s+and\s+decision\s+impact\.?$/i;

function norm(s: string | undefined | null): string {
  return (s ?? '').trim().replace(/\s+/g, ' ').replace(/\.\s*$/, '').toLowerCase();
}

/** The "Ranked #N …" template rank, else null. */
export function extractRank(whyKey: string | undefined | null): number | null {
  const m = RANKED_WHYKEY_RE.exec((whyKey ?? '').trim());
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

/** True when an INFLUENCES value is a known template phrase. */
export function isGenericInfluences(s: string | undefined | null): boolean {
  const n = norm(s);
  return n.length > 0 && GENERIC_INFLUENCES.includes(n);
}

/** True when a WHY-KEY value is a known template phrase (incl. the Ranked #N family). */
export function isGenericWhyKey(s: string | undefined | null): boolean {
  const n = norm(s);
  if (n.length === 0) return false;
  return GENERIC_WHYKEY.includes(n) || RANKED_WHYKEY_RE.test((s ?? '').trim());
}

/** Title-case a compact SOURCE token ("GRID" → "Grid", "power-btm" → "Power-Btm").
 *  Leaves multi-word evidence labels (with spaces) as-is beyond first-letter casing. */
function titleCaseSource(src: string): string {
  return src
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, (c) => c.toUpperCase());
}

export interface FindingMetaRow {
  label: string;
  value: string;
}

export interface FindingMeta {
  /** Compact one-liner: `Source: Grid · Ranked #1` (or a subset), else null. */
  metaLine: string | null;
  /** Non-generic influences/whyKey that genuinely add information — kept as rows. */
  extraRows: FindingMetaRow[];
}

/**
 * Derive the collapsed meta for each finding IN ORDER. A value is treated as
 * generic when it matches a known template phrase OR appears verbatim on ≥2
 * findings in this report (shared boilerplate). The rank is lifted from the
 * "Ranked #N" template into the compact line; the source is title-cased.
 */
export function deriveFindingMeta(findings: readonly ContextualKeyFinding[]): FindingMeta[] {
  // Shared-string sets: any influences/whyKey value ≥2 findings carry verbatim
  // is boilerplate regardless of whether it matches a known phrase.
  const inflCounts = new Map<string, number>();
  const whyCounts = new Map<string, number>();
  for (const f of findings) {
    const i = norm(f.influences);
    if (i) inflCounts.set(i, (inflCounts.get(i) ?? 0) + 1);
    const w = norm(f.whyKey);
    if (w) whyCounts.set(w, (whyCounts.get(w) ?? 0) + 1);
  }
  const sharedInfl = (s: string | undefined | null): boolean => (inflCounts.get(norm(s)) ?? 0) >= 2;
  const sharedWhy = (s: string | undefined | null): boolean => (whyCounts.get(norm(s)) ?? 0) >= 2;

  return findings.map((f) => {
    // The source is the single compact provenance signal; findings are already
    // ordered by priority, so the "Ranked #N" whyKey is treated as generic and
    // dropped rather than re-surfaced.
    const metaLine = f.source && f.source.trim() ? `Source: ${titleCaseSource(f.source)}` : null;

    const extraRows: FindingMetaRow[] = [];
    if (f.influences && !isGenericInfluences(f.influences) && !sharedInfl(f.influences)) {
      extraRows.push({ label: 'Influences', value: f.influences });
    }
    // `isGenericWhyKey` already matches the "Ranked #N …" template, so a
    // templated rank never survives here; only bespoke why-key prose does.
    if (f.whyKey && !isGenericWhyKey(f.whyKey) && !sharedWhy(f.whyKey)) {
      extraRows.push({ label: 'Why key', value: f.whyKey });
    }
    return { metaLine, extraRows };
  });
}
